// Capture a small still frame from a local video File as a JPEG data URL. Used
// for a temporary card thumbnail while the server-side (Cloudflare Stream)
// thumbnail is still being generated. The frame is downscaled, so we keep a few
// KB — never the multi-GB source blob (a File streams from disk on demand).
export function videoFrameThumbnail(file, { seekRatio = 0.1, maxSize = 320, quality = 0.7, timeoutMs = 10000 } = {}) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';

        // Settle once: detaching the handlers first stops the cleanup load()
        // from re-firing onerror and settling the promise a second time.
        let settled = false;
        const finish = (result, err) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            video.onloadedmetadata = video.onseeked = video.onerror = null;
            URL.revokeObjectURL(url);
            video.removeAttribute('src');
            video.load();
            err ? reject(err) : resolve(result);
        };

        // A stuck decode would otherwise leak the <video> element and the blob.
        const timer = setTimeout(() => finish(null, new Error('video thumbnail timeout')), timeoutMs);

        video.onloadedmetadata = () => {
            // Nudge past the very first frame — it is often black.
            video.currentTime = Math.min(1, (video.duration || 0) * seekRatio) || 0.1;
        };

        video.onseeked = () => {
            try {
                const { videoWidth: w, videoHeight: h } = video;
                if (!w || !h) return finish(null, new Error('no video dimensions'));

                const scale = Math.min(1, maxSize / Math.max(w, h));
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(w * scale);
                canvas.height = Math.round(h * scale);
                canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
                finish(canvas.toDataURL('image/jpeg', quality));
            } catch (err) {
                finish(null, err);
            }
        };

        video.onerror = () => finish(null, new Error('video thumbnail failed'));

        // Attach src last so no event fires before its handler is wired.
        video.src = url;
    });
}
